import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Building2, Truck, CreditCard, Calendar, DollarSign, Download } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../hooks/use-toast';
import BackButton from '../../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  sent_to_supplier: { label: 'Sent to Supplier', color: 'bg-blue-100 text-blue-800' },
  supplier_confirmed: { label: 'Supplier Confirmed', color: 'bg-indigo-100 text-indigo-800' },
  partially_delivered: { label: 'Partially Delivered', color: 'bg-purple-100 text-purple-800' },
  fully_delivered: { label: 'Fully Delivered', color: 'bg-green-100 text-green-800' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800' }
};

const PurchaseOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchPO();
  }, [id]);

  const fetchPO = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/procurement/purchase-orders/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch PO');
      const data = await response.json();
      setPo(data);
    } catch (error) {
      console.error('Error fetching PO:', error);
      toast({
        title: 'Error',
        description: 'Failed to load purchase order',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowAction = async (action) => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/purchase-orders/${id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      if (!response.ok) throw new Error(`Failed to ${action}`);

      toast({
        title: 'Success',
        description: `Action ${action} completed successfully`
      });

      fetchPO();
    } catch (error) {
      console.error(`Error ${action}:`, error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/purchase-orders/${id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to download PDF');

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${po?.po_number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: 'PDF downloaded successfully'
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to download PDF',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div>
        <BackButton />
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading purchase order...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!po) {
    return (
      <div>
        <BackButton />
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Purchase order not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <FileText className="h-8 w-8 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">{po.po_number}</h2>
              <div className="flex items-center space-x-2 mt-1">
                {getStatusBadge(po.status)}
                <span className="text-sm text-[#6C757D]">
                  {formatDate(po.po_date)}
                </span>
              </div>
            </div>
          </div>

          {/* Workflow Actions */}
          <div className="flex space-x-2">
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8] hover:text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            {po.status === 'draft' && (
              <Button onClick={() => handleWorkflowAction('submit-for-approval')} className="bg-[#00B4D8]">
                Submit for Approval
              </Button>
            )}
            {po.status === 'approved' && (
              <Button onClick={() => handleWorkflowAction('send-to-supplier')} className="bg-blue-600">
                Send to Supplier
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Company & Supplier Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              {po.company_snapshot?.logo_base64 && (
                <img
                  src={po.company_snapshot.logo_base64}
                  alt="Company logo"
                  className="h-12 w-12 object-contain rounded border border-gray-200 p-1 bg-white"
                />
              )}
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Building2 className="h-4 w-4 text-[#00B4D8]" />
                  <h3 className="text-sm font-semibold text-[#2B2D42]">Issuing Company</h3>
                </div>
                <p className="text-sm text-[#2B2D42] font-medium">
                  {po.company_snapshot?.legal_name}
                </p>
                <p className="text-xs text-[#6C757D] mt-1">
                  {po.company_snapshot?.address}, {po.company_snapshot?.city}
                </p>
                <p className="text-xs text-[#6C757D]">
                  {po.company_snapshot?.country}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Truck className="h-4 w-4 text-[#00B4D8]" />
                  <h3 className="text-sm font-semibold text-[#2B2D42]">Supplier</h3>
                </div>
                <p className="text-sm text-[#2B2D42] font-medium">
                  {po.supplier_snapshot?.company_name}
                </p>
                <p className="text-xs text-[#6C757D] mt-1">
                  {po.supplier_snapshot?.email}
                </p>
                <p className="text-xs text-[#6C757D]">
                  {po.supplier_snapshot?.phone}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <CreditCard className="h-4 w-4 text-[#00B4D8]" />
                  <h3 className="text-sm font-semibold text-[#2B2D42]">Bank Account</h3>
                </div>
                <p className="text-sm text-[#2B2D42] font-medium">
                  {po.bank_account_snapshot?.bank_name}
                </p>
                <p className="text-xs text-[#6C757D] mt-1">
                  {po.bank_account_snapshot?.account_number}
                </p>
                <p className="text-xs text-[#6C757D]">
                  {po.bank_account_snapshot?.currency}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Items</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-sm font-medium text-[#6C757D] pb-3">Product</th>
                  <th className="text-right text-sm font-medium text-[#6C757D] pb-3">Qty</th>
                  <th className="text-right text-sm font-medium text-[#6C757D] pb-3">Unit Price</th>
                  <th className="text-right text-sm font-medium text-[#6C757D] pb-3">Tax</th>
                  <th className="text-right text-sm font-medium text-[#6C757D] pb-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {po.items.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 text-sm text-[#2B2D42]">{item.product_name}</td>
                    <td className="py-3 text-sm text-[#2B2D42] text-right">{item.quantity} {item.unit}</td>
                    <td className="py-3 text-sm text-[#2B2D42] text-right">
                      {formatCurrency(item.unit_price, po.currency)}
                    </td>
                    <td className="py-3 text-sm text-[#2B2D42] text-right">
                      {formatCurrency(item.tax_amount, po.currency)}
                    </td>
                    <td className="py-3 text-sm text-[#2B2D42] font-medium text-right">
                      {formatCurrency(item.total, po.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="max-w-xs ml-auto space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#6C757D]">Subtotal:</span>
                <span className="text-[#2B2D42] font-medium">
                  {formatCurrency(po.subtotal, po.currency)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#6C757D]">Tax Total:</span>
                <span className="text-[#2B2D42] font-medium">
                  {formatCurrency(po.tax_total, po.currency)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#6C757D]">Shipping:</span>
                <span className="text-[#2B2D42] font-medium">
                  {formatCurrency(po.shipping_cost, po.currency)}
                </span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                <span className="text-[#2B2D42]">Total:</span>
                <span className="text-[#00B4D8]">
                  {formatCurrency(po.total_amount, po.currency)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {po.notes && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold text-[#2B2D42] mb-2">Notes</h3>
            <p className="text-sm text-[#6C757D]">{po.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PurchaseOrderDetails;
